'use client';

import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { feature, mesh } from 'topojson-client';
import ControlPanel, {
  DEFAULT_SETTINGS,
  type GlobeSettings,
  type ColorTheme,
} from './ControlPanel';

// ── Types ──────────────────────────────────────────────────────────────────

interface WindHeader {
  la1: number; lo1: number;
  dx: number;  dy: number;
  nx: number;  ny: number;
}

interface WindData {
  header: WindHeader;
  uData: number[];
  vData: number[];
}

interface Particle {
  x: number; y: number;
  px: number; py: number;
  age: number;
  maxAge: number;
  speed: number;
  color?: string;
  dirU: number;
  dirV: number;
}

interface Star {
  x: number; y: number;
  size: number;
  baseAlpha: number;
  color: string;
  layer: number;
  twinkleSpeed: number;
  twinklePhase: number;
}

interface ShootingStar {
  x: number; y: number;
  dx: number; dy: number;
  age: number;
  maxAge: number;
  size: number;
}

// ── Color themes ───────────────────────────────────────────────────────────

type ColorTheme =
  'classic'|'ocean'|'fire'|'neon'|'aurora'|'mono'|'iridescence';

const COLOR_THEMES: Record<ColorTheme, [number, [number,number,number]][]> = {
  classic: [
    [0, [17,110,187]],[3,[44,183,168]],[7,[76,196,123]],
    [14,[170,213,57]],[21,[253,214,43]],[28,[240,124,26]],
    [40,[212,55,21]], [63,[130,0,40]],
  ],
  ocean: [
    [0,[0,20,60]],   [5,[0,60,120]],  [12,[0,110,180]],
    [20,[0,170,220]],[30,[50,210,240]],[50,[150,235,255]],
    [63,[220,250,255]],
  ],
  fire: [
    [0,[10,0,30]],   [2,[60,0,10]],   [5,[150,20,0]],
    [10,[210,70,0]], [18,[250,140,0]], [30,[255,210,0]],
    [50,[255,250,200]],[63,[255,255,255]],
  ],
  neon: [
    [0,[0,0,50]],    [4,[30,0,120]],  [8,[0,50,220]],
    [14,[0,200,255]],[20,[0,255,100]],[30,[200,255,0]],
    [45,[255,100,0]],[63,[255,0,150]],
  ],
  aurora: [
    [0,[5,10,30]],   [4,[0,60,80]],   [8,[0,120,100]],
    [14,[0,200,150]],[22,[100,230,200]],[35,[180,160,255]],
    [50,[220,100,255]],[63,[255,200,255]],
  ],
  mono: [
    [0,  [15,  15,  20]],
    [5,  [55,  58,  68]],
    [12, [110, 115, 128]],
    [20, [170, 175, 188]],
    [30, [210, 215, 228]],
    [45, [235, 240, 250]],
    [63, [255, 255, 255]],
  ],
  iridescence: [
    [0,  [180,  0, 255]],
    [5,  [  0, 80, 255]],
    [12, [  0,220, 255]],
    [22, [  0, 255, 120]],
    [32, [140, 255,   0]],
    [45, [255, 210,   0]],
    [55, [255,  60,   0]],
    [63, [255,   0, 180]],
  ],
};

function windColor(speed: number, theme: ColorTheme): string {
  const stops = COLOR_THEMES[theme];
  if (speed <= 0) return `rgb(${stops[0][1].join(',')})`;
  for (let i = 1; i < stops.length; i++) {
    const [s0,c0] = stops[i-1], [s1,c1] = stops[i];
    if (speed <= s1) {
      const t = (speed-s0)/(s1-s0);
      return `rgb(${~~(c0[0]+t*(c1[0]-c0[0]))},${~~(c0[1]+t*(c1[1]-c0[1]))},${~~(c0[2]+t*(c1[2]-c0[2]))})`;
    }
  }
  const last = stops[stops.length-1][1];
  return `rgb(${last.join(',')})`;
}

const MAX_WIND_SPEED   = 63;
const N_COLOR_BUCKETS  = 100;
const MAX_AGE          = 100;
const EARTH_R          = 6_371_000;

function interpolateWind(wind: WindData, lon: number, lat: number): [number, number] | null {
  const { header, uData, vData } = wind;
  const { lo1, la1, dx, dy, nx, ny } = header;
  let x = (lon - lo1) / dx;
  let y = (la1 - lat) / dy;
  x = ((x % nx) + nx) % nx;
  if (y < 0 || y > ny - 1) return null;
  const x0 = Math.floor(x), x1 = (x0 + 1) % nx;
  const y0 = Math.floor(y), y1 = Math.min(y0 + 1, ny - 1);
  const tx = x - x0, ty = y - y0;
  const idx = (xi: number, yi: number) => yi * nx + xi;
  const u =
    uData[idx(x0, y0)] * (1 - tx) * (1 - ty) +
    uData[idx(x1, y0)] * tx       * (1 - ty) +
    uData[idx(x0, y1)] * (1 - tx) * ty       +
    uData[idx(x1, y1)] * tx       * ty;
  const v =
    vData[idx(x0, y0)] * (1 - tx) * (1 - ty) +
    vData[idx(x1, y0)] * tx       * (1 - ty) +
    vData[idx(x0, y1)] * (1 - tx) * ty       +
    vData[idx(x1, y1)] * tx       * ty;
  return [u, v];
}

// CDN fallback for world topology (Natural Earth 110m)
const WORLD_TOPO_URL =
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ── Component ──────────────────────────────────────────────────────────────

export default function Globe({ onReady }: { onReady?: () => void }) {
  const bgCanvasRef       = useRef<HTMLCanvasElement>(null);
  const globeCanvasRef    = useRef<HTMLCanvasElement>(null);
  const particleCanvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef            = useRef<number>(0);
  const readoutRef        = useRef<HTMLDivElement>(null);
  const settingsRef       = useRef<GlobeSettings>({ ...DEFAULT_SETTINGS });
  const [num8kLoaded, setNum8kLoaded] = useState(0);
  const [capturing, setCapturing]     = useState(false);
  const [sharing, setSharing]         = useState(false);

  function buildCompositeBlob(): Promise<Blob | null> {
    return new Promise(resolve => {
      const bg  = bgCanvasRef.current;
      const gl  = globeCanvasRef.current;
      const pt  = particleCanvasRef.current;
      if (!bg || !gl || !pt) { resolve(null); return; }

      let W = bg.width;
      let H = bg.height;

      const out = document.createElement('canvas');
      out.width  = W;
      out.height = H;
      const ctx = out.getContext('2d')!;

      // Composite all three layers in order
      ctx.drawImage(bg, 0, 0);
      ctx.drawImage(gl, 0, 0);
      ctx.drawImage(pt, 0, 0);

      // ── Branding overlay ──────────────────────────────────────────────
      const PAD   = 20;

      // Semi-transparent pill background
      const pillW = 210;
      const pillH = 44;
      const pillX = W - pillW - PAD;
      const pillY = H - pillH - PAD;

      ctx.save();
      ctx.globalAlpha = 0.82;
      ctx.fillStyle   = 'rgba(4,10,28,0.88)';
      const r = 12;
      ctx.beginPath();
      ctx.moveTo(pillX + r, pillY);
      ctx.lineTo(pillX + pillW - r, pillY);
      ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
      ctx.lineTo(pillX + pillW, pillY + pillH - r);
      ctx.quadraticCurveTo(pillX + pillW, pillY + pillH, pillX + pillW - r, pillY + pillH);
      ctx.lineTo(pillX + r, pillY + pillH);
      ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - r);
      ctx.lineTo(pillX, pillY + r);
      ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      // Teal accent border on pill
      ctx.save();
      ctx.globalAlpha = 0.55;
      ctx.strokeStyle = '#00e5cc';
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.moveTo(pillX + r, pillY);
      ctx.lineTo(pillX + pillW - r, pillY);
      ctx.quadraticCurveTo(pillX + pillW, pillY, pillX + pillW, pillY + r);
      ctx.lineTo(pillX + pillW, pillY + pillH - r);
      ctx.quadraticCurveTo(pillX + pillW, pillY + pillH, pillX + pillW - r, pillY + pillH);
      ctx.lineTo(pillX + r, pillY + pillH);
      ctx.quadraticCurveTo(pillX, pillY + pillH, pillX, pillY + pillH - r);
      ctx.lineTo(pillX, pillY + r);
      ctx.quadraticCurveTo(pillX, pillY, pillX + r, pillY);
      ctx.closePath();
      ctx.stroke();
      ctx.restore();

      // Logo arc
      const lx = pillX + 18;
      const ly = pillY + pillH / 2 + 2;
      const lr = 9;
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = 'rgba(255,255,255,0.88)';
      ctx.lineWidth   = 1.5;
      ctx.beginPath();
      ctx.arc(lx, ly, lr, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = '#00e5cc';
      ctx.lineWidth   = 1.8;
      ctx.beginPath();
      ctx.arc(lx, ly - 2, lr + 2, Math.PI + 0.35, -0.35, false);
      ctx.stroke();
      ctx.restore();

      // "ATMOSPHERE" text
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.font        = '300 11px "Inter", system-ui, sans-serif';
      ctx.fillStyle   = 'rgba(255,255,255,0.9)';
      ctx.letterSpacing = '0.1em';
      ctx.fillText('ATMOSPHERE', pillX + 36, pillY + 19);
      ctx.restore();

      // "8K" text in teal
      ctx.save();
      ctx.globalAlpha = 1;
      ctx.font        = 'bold 11px "Inter", system-ui, sans-serif';
      ctx.fillStyle   = '#00e5cc';
      ctx.fillText('8K', pillX + 36 + 82, pillY + 19);
      ctx.restore();

      // Subtitle
      ctx.save();
      ctx.globalAlpha = 0.55;
      ctx.font        = '400 9px system-ui, sans-serif';
      ctx.fillStyle   = 'rgba(255,255,255,0.5)';
      ctx.fillText('atmosphere8k.space', pillX + 36, pillY + 33);
      ctx.restore();

      // Timestamp
      const now = new Date();
      const ts  = `${now.getUTCFullYear()}-${String(now.getUTCMonth()+1).padStart(2,'0')}-${String(now.getUTCDate()).padStart(2,'0')} ${String(now.getUTCHours()).padStart(2,'0')}:${String(now.getUTCMinutes()).padStart(2,'0')} UTC`;
      ctx.save();
      ctx.globalAlpha = 0.35;
      ctx.font        = '400 9px system-ui, sans-serif';
      ctx.fillStyle   = 'rgba(255,255,255,0.4)';
      ctx.fillText(ts, PAD, H - PAD);
      ctx.restore();

      out.toBlob(blob => resolve(blob), 'image/png');
    });
  }

  function captureGlobe() {
    setCapturing(true);
    buildCompositeBlob().then(blob => {
      if (!blob) { setCapturing(false); return; }
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `atmosphere8k-${Date.now()}.png`;
      a.click();
      URL.revokeObjectURL(url);
      setCapturing(false);
    });
  }

  async function shareGlobe() {
    setSharing(true);
    try {
      const blob = await buildCompositeBlob();
      if (!blob) { setSharing(false); return; }

      const file = new File([blob], 'atmosphere8k.png', { type: 'image/png' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Atmosphere8K — Live Wind Globe',
          text:  'Check out this live global wind visualization at atmosphere8k.space',
          url:   'https://atmosphere8k.space',
          files: [file],
        });
      } else if (navigator.clipboard && (navigator.clipboard as any).write) {
        // Fallback: copy image to clipboard
        await (navigator.clipboard as any).write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        // Brief visual feedback — reuse sharing state (button label changes)
        setTimeout(() => setSharing(false), 1800);
        return;
      } else {
        // Last resort: download
        const url = URL.createObjectURL(blob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `atmosphere8k-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (_) {
      // User cancelled share or error — silently ignore
    }
    setSharing(false);
  }

  useEffect(() => {
    const gCanvas = globeCanvasRef.current!;
    const pCanvas = particleCanvasRef.current!;
    const gCtx    = gCanvas.getContext('2d')!;
    const pCtx    = pCanvas.getContext('2d')!;

    let rotation: [number, number, number] = [0, -30, 0];
    let zoom      = 1;
    let wind: WindData | null = null;
    let world: any = null;
    let particles: Particle[] = [];
    let W = 0, H = 0;
    let dragging  = false;
    let lastX     = 0, lastY = 0;
    let lastPinch = 0;
    let running   = false;

    // Bucket colors are rebuilt per-frame based on current theme
    let BUCKET_COLORS: string[] = Array.from({ length: N_COLOR_BUCKETS }, (_, b) =>
      windColor((b + 0.5) * (MAX_WIND_SPEED / N_COLOR_BUCKETS), settingsRef.current.colorTheme)
    );
    let lastTheme: ColorTheme = settingsRef.current.colorTheme;

    function resize() {
      W = gCanvas.width = pCanvas.width = bgCanvasRef.current!.width  = window.innerWidth;
      H = gCanvas.height = pCanvas.height = bgCanvasRef.current!.height = window.innerHeight;
      pCtx.clearRect(0, 0, W, H);
    }
    resize();
    window.addEventListener('resize', resize);

    // ── Deep space background ─────────────────────────────────────────
    const bgCanvas = bgCanvasRef.current!;
    const bgCtx    = bgCanvas.getContext('2d')!;

    const STAR_COLORS = [
      '#ffffff','#ffffff','#ffffff','#ffffff','#ffffff', // pure white (dominant)
      '#f0f4ff','#f0f4ff','#f0f4ff',                   // blue-white
      '#e8f0ff','#e8f0ff',                               // faint blue
      '#fff8f0',                                         // barely warm
      '#c8d8ff',                                         // hot blue (rare)
      '#ffcc99',                                         // orange (very rare, small only)
    ];

    const stars: Star[] = [];

    // Background field — 2500 random stars
    for (let i = 0; i < 4000; i++) {
      const r = Math.random();
      stars.push({
        x: Math.random(),
        y: Math.random(),
        size:
          r < 0.78 ? 0.15 + Math.random() * 0.25 :
          r < 0.96 ? 0.4  + Math.random() * 0.3  :
                     0.7  + Math.random() * 0.4,
        baseAlpha:    0.65 + Math.random() * 0.35,
        color:        STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)],
        layer:        Math.floor(Math.random() * 3),
        twinkleSpeed: 0.4 + Math.random() * 2.5,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }

    // Milky Way — 900 extra faint stars along a diagonal band
    for (let i = 0; i < 900; i++) {
      const t = Math.random();
      stars.push({
        x: t,
        y: Math.max(0, Math.min(1, 0.12 + t * 0.65 + (Math.random() - 0.5) * 0.28)),
        size:         0.12 + Math.random() * 0.28,
        baseAlpha:    0.4  + Math.random() * 0.45,
        color:        Math.random() < 0.35 ? '#c8d0ff' : '#e0e4ff',
        layer:        0,
        twinkleSpeed: 0.2 + Math.random() * 0.8,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }

    let shootingStars: ShootingStar[] = [];
    let nextShootingAt = Date.now() + 6000 + Math.random() * 10000;

    // ── Planet textures ─────────────────────────────────────────────────
    const planetTextures: Record<string, HTMLImageElement> = {};
    const PLANETS = ['earth', 'moon', 'mars', 'venus', 'jupiter', 'sun', 'lava', '2322', 'cares', 'neptune', 'tethys', 'oceanless'];

    let currentTexQuality = '2k';

    function texUrl(name: string, q: string): string {
      // Local public asset — bypass proxy entirely
      if (name === 'lava') return '/assets/images/lava_planet_map_2k-1787610470678.jpg';
      if (name === '2322') return '/assets/images/2322_map_2k-1787664197282.png';
      if (name === 'cares') return '/assets/images/ceres_map_2k-1787683389001.png';
      if (name === 'neptune') return '/assets/images/neptune_map_2k-1787683383642.jpg';
      if (name === 'tethys') return '/assets/images/tethys_map_2k-1787764168653.jpg';
      if (name === 'oceanless') return '/assets/images/oceanless_map_2k-1787683392572.png';
      return q === '8k'
        ? `/api/texture/${name}?quality=8k`
        : `/api/texture/${name}`;
    }

    const texDataCache: Record<string, ImageData> = {};
    let texOffscreen: HTMLCanvasElement | null = null;
    let texOffCtx:    CanvasRenderingContext2D | null = null;
    let lastTexRot    = [Infinity, Infinity];
    let lastTexPlanet = '';
    const TEX_SCALE   = 0.65;

    PLANETS.forEach(name => {
      const img = new Image();
      img.onload = () => {
        delete texDataCache[name];
        texOffscreen  = null;
        lastTexPlanet = '';
        console.log(`[globe] Loaded 2k: ${name}`);
      };
      img.onerror = () =>
        console.error(`[globe] Failed 2k: ${name}`);
      img.src = texUrl(name, '2k');
      planetTextures[name] = img;
    });

    // Pre-load 8k silently in background
    // Sequential: Moon first, then the rest
    const PLANETS_8K_ORDER = ['earth', 'moon', 'mars', 'venus', 'jupiter', 'sun'];
    const planetTextures8k: Record<string, HTMLImageElement> = {};

    function preload8kSequential(index: number) {
      if (index >= PLANETS_8K_ORDER.length) return;
      const name = PLANETS_8K_ORDER[index];
      const img  = new Image();
      img.onload = () => {
        planetTextures8k[name] = img;
        delete texDataCache[`${name}_8k`];
        console.log(`[globe] Pre-loaded 8k: ${name}`);
        setNum8kLoaded(prev => prev + 1);
        preload8kSequential(index + 1); // load next after this one
      };
      img.onerror = () => {
        console.error(`[globe] 8k pre-load failed: ${name}`);
        setNum8kLoaded(prev => prev + 1); // count errors too so indicator clears
        preload8kSequential(index + 1); // skip to next on error
      };
      img.src = texUrl(name, '8k');
    }

    // Start after 3 seconds — 2k images get a head start
    setTimeout(() => preload8kSequential(0), 3000);

    const bgImages: Record<string, HTMLImageElement> = {};
    const BG_URLS: Record<string, string> = {
      milkyway:  '/assets/images/MilkyWay-1787779496051.jpg',
      heart:     '/assets/images/Heart-1787779503437.jpg',
      heartsoul: '/assets/images/Heart_Soul-1787779528495.jpg',
    };

    // ── Per-pixel spherical texture renderer ──────────────────────────
    function updateTextureOffscreen() {
      const planet = settingsRef.current.planetSurface;
      if (planet === 'earth') {
        const q = (settingsRef.current as any).textureQuality ?? '2k';
        // 8k already preloaded — skip the 2k on-demand check entirely
        if (q === '8k' && planetTextures8k['earth']?.complete
            && (planetTextures8k['earth']?.naturalWidth ?? 0) > 0) {
          // 8k is ready — fall through to activeTex selection below
        } else {
          // On-demand 2k load
          if (!planetTextures['earth']) {
            const img = new Image();
            img.onload = () => {
              delete texDataCache['earth_2k'];
              texOffscreen  = null;
              lastTexPlanet = '';
            };
            img.src = texUrl('earth', '2k');
            planetTextures['earth'] = img;
          }
          const earthTex = planetTextures['earth'];
          if (!earthTex?.complete || !earthTex?.naturalWidth) return;
        }
      }

      const quality = (settingsRef.current as any).textureQuality ?? '2k';

      if ((settingsRef.current as any).needsTextureReload || quality !== currentTexQuality) {
        (settingsRef.current as any).needsTextureReload = false;
        currentTexQuality = quality;
        PLANETS.forEach(name => {
          delete texDataCache[name];
          const img  = new Image();
          img.onload = () => {
            delete texDataCache[name];
            texOffscreen  = null;
            lastTexPlanet = '';
            console.log(`[globe] Loaded 8k: ${name}`);
          };
          img.onerror = () =>
            console.error(`[globe] Failed 8k: ${name}`);
          img.src = texUrl(name, quality);
          planetTextures[name] = img;
        });
        texOffscreen  = null;
        lastTexPlanet = '';
        return;
      }

      const rotChanged =
        Math.abs(rotation[0] - lastTexRot[0]) > 0.08 ||
        Math.abs(rotation[1] - lastTexRot[1]) > 0.08;
      const planetChanged = lastTexPlanet !== planet;
      if (!rotChanged && !planetChanged && texOffscreen) return;

      // Use 8k image if quality=8k and it is ready,
      // otherwise fall back to 2k
      const activeTex = (quality === '8k' && planetTextures8k[planet]?.complete
                         && planetTextures8k[planet]?.naturalWidth > 0)
        ? planetTextures8k[planet]
        : planetTextures[planet];

      if (!activeTex?.complete || !activeTex.naturalWidth) return;

      const cacheKey = `${planet}_${quality === '8k'
        && planetTextures8k[planet]?.complete ? '8k' : '2k'}`;

      if (!texDataCache[cacheKey]) {
        console.log(`[globe] Building ${cacheKey} cache…`);
        const tc   = document.createElement('canvas');
        // NO CAP — use full source resolution
        tc.width   = activeTex.naturalWidth;
        tc.height  = activeTex.naturalHeight;
        const tctx = tc.getContext('2d')!;
        tctx.drawImage(activeTex, 0, 0, tc.width, tc.height);
        const raw = tctx.getImageData(0, 0, tc.width, tc.height);

        const PLANET_LEVELS: Record<string, { contrast: number; brightness: number }> = {
  earth:   { contrast: 1.0,  brightness: 85  },
  moon:    { contrast: 1.55, brightness: -12 },
  mars:    { contrast: 1.2,  brightness: 80  },
  venus:   { contrast: 1.1,  brightness: 65  },
  jupiter: { contrast: 1.2,  brightness: 85  },
  sun:     { contrast: 1.0,  brightness: 80  },
  lava:    { contrast: 1.25, brightness: 80  },
  2322:    { contract: 1.25, brightness: 75  },
  cares:   { contrast: 2.0,  brightness: 95  },
  neptune: { contrast: 1.5, brightness:  85  },
  tethys:  { contrast: 1.25, brightness:  85 },
  oceanless: { contrast: 1.25, brightness:  75 },
};
        const tc2   = document.createElement('canvas');
        tc2.width   = tc.width;
        tc2.height  = tc.height;
        const tctx2 = tc2.getContext('2d')!;
        tctx2.drawImage(tc, 0, 0, tc.width, tc.height);
        let raw2 = tctx2.getImageData(0, 0, tc.width, tc.height);
        const levels = PLANET_LEVELS[planet];
        if (levels) {
          tctx2.filter = `contrast(${levels.contrast}) brightness(${levels.brightness}%)`;
          tctx2.drawImage(tc, 0, 0, tc.width, tc.height);
          raw2 = tctx2.getImageData(0, 0, tc.width, tc.height);
        }
        texDataCache[cacheKey] = raw2;
      }

      const td = texDataCache[cacheKey];
      const TW = td.width, TH = td.height;
      const scale = (quality === '8k'
        && planetTextures8k[planet]?.complete) ? 0.85 : 0.65;
      const rLow  = Math.ceil(globeRadius() * scale);
      const size  = rLow * 2;

      // Create or resize offscreen canvas
      if (!texOffscreen) {
        texOffscreen = document.createElement('canvas');
        texOffCtx    = texOffscreen.getContext('2d')!;
      }
      texOffscreen.width  = size;
      texOffscreen.height = size;

      // D3 projection at reduced resolution
      const lowProj = d3.geoOrthographic()
        .scale(rLow)
        .translate([rLow, rLow])
        .rotate(rotation as [number, number, number])
        .clipAngle(90);

      const imgData = texOffCtx!.createImageData(size, size);
      const buf     = imgData.data;
      const rLow2   = rLow * rLow;

      for (let py = 0; py < size; py++) {
        for (let px = 0; px < size; px++) {
          const dx = px - rLow, dy = py - rLow;
          if (dx * dx + dy * dy > rLow2) continue;

          const ll = lowProj.invert!([px, py]);
          if (!ll || isNaN(ll[0]) || isNaN(ll[1])) continue;

          // Sample equirectangular texture at (lon, lat)
          const tx = Math.floor(
            (((ll[0] + 180) / 360 * TW) % TW + TW) % TW
          );
          const ty = Math.max(0, Math.min(TH - 1,
            Math.floor((90 - ll[1]) / 180 * TH)
          ));

          const si = (ty * TW + tx) * 4;
          const di = (py * size  + px) * 4;
          buf[di]     = td.data[si];
          buf[di + 1] = td.data[si + 1];
          buf[di + 2] = td.data[si + 2];
          buf[di + 3] = 255;
        }
      }

      texOffCtx!.putImageData(imgData, 0, 0);
      lastTexRot    = [rotation[0], rotation[1]];
      lastTexPlanet = planet;
    }

    // Parallax constants per depth layer
    const PARALLAX = [0.012, 0.035, 0.08];

    // ── drawBackground ────────────────────────────────────────────────
    function drawBackground(timestamp: number) {
      bgCtx.clearRect(0, 0, W, H);

      const scene = (settingsRef.current as any).bgScene ?? 'stars';

      // ── Image background ──────────────────────────────────────────────
      if (scene !== 'stars' && BG_URLS[scene]) {
        if (!bgImages[scene]) {
          const img  = new Image();
          img.onload = () => { bgImages[scene] = img; };
          img.src    = BG_URLS[scene];
        }
        const img = bgImages[scene];
        if (img?.complete && img?.naturalWidth) {
          const scale = Math.max(W / img.naturalWidth, H / img.naturalHeight);
          const sw    = img.naturalWidth  * scale;
          const sh    = img.naturalHeight * scale;
          bgCtx.drawImage(img, (W - sw) / 2, (H - sh) / 2, sw, sh);
        } else {
          bgCtx.fillStyle = '#00000a';
          bgCtx.fillRect(0, 0, W, H);
        }
      } else {
        bgCtx.fillStyle = '#00000a';
        bgCtx.fillRect(0, 0, W, H);
      }

      // ── Milky Way glow band (stars mode only) ─────────────────────────
      if (scene === 'stars') {
        bgCtx.save();
        bgCtx.translate(W / 2, H / 2);
        bgCtx.rotate(0.44);
        const mwW = W * 2, mwH = H * 0.55;
        const mwGrad = bgCtx.createLinearGradient(0, -mwH/2, 0, mwH/2);
        mwGrad.addColorStop(0,   'rgba(50,40,90,0)');
        mwGrad.addColorStop(0.25,'rgba(60,48,100,0.055)');
        mwGrad.addColorStop(0.5, 'rgba(75,60,120,0.09)');
        mwGrad.addColorStop(0.75,'rgba(60,48,100,0.055)');
        mwGrad.addColorStop(1,   'rgba(50,40,90,0)');
        bgCtx.fillStyle = mwGrad;
        bgCtx.fillRect(-mwW/2, -mwH/2, mwW, mwH);
        bgCtx.restore();
      }

      // ── Stars (reduced opacity over images) ───────────────────────────
      const starAlpha = scene === 'stars' ? 1 : 0.35;
      bgCtx.globalAlpha = starAlpha;

      for (const s of stars) {
        const parallaxX = (rotation[0] / 360) * PARALLAX[s.layer];
        const parallaxY = (rotation[1] / 180) * PARALLAX[s.layer] * 0.4;
        let px = ((s.x + parallaxX) % 1 + 1) % 1;
        let py = s.y + parallaxY;
        if (py < 0 || py > 1) continue;
        const twinkle = 0.78 + 0.22 * Math.sin(
          timestamp * 0.001 * s.twinkleSpeed + s.twinklePhase
        );
        bgCtx.beginPath();
        bgCtx.arc(px * W, py * H, s.size, 0, Math.PI * 2);
        bgCtx.fillStyle = s.color;
        bgCtx.globalAlpha = s.baseAlpha * twinkle * starAlpha;
        bgCtx.fill();
      }

      bgCtx.globalAlpha = 1;

      // ── Shooting stars ────────────────────────────────────────────────
      const now = Date.now();
      if (now > nextShootingAt && shootingStars.length < 3) {
        const angle = 0.3 + Math.random() * 0.4;
        const speed = 3.5 + Math.random() * 4;
        shootingStars.push({
          x: Math.random() * W * 0.85, y: Math.random() * H * 0.45,
          dx: Math.cos(angle) * speed, dy: Math.sin(angle) * speed,
          age: 0, maxAge: 45 + Math.floor(Math.random() * 40),
          size: 0.8 + Math.random() * 1.4,
        });
        nextShootingAt = now + 10000 + Math.random() * 18000;
      }
      shootingStars = shootingStars.filter(ss => ss.age < ss.maxAge);
      for (const ss of shootingStars) {
        const progress = ss.age / ss.maxAge;
        const alpha    = progress < 0.15
          ? progress / 0.15
          : 1 - ((progress - 0.15) / 0.85);
        const tailFrames = Math.min(ss.age, 22);
        const x1 = ss.x + ss.dx * ss.age;
        const y1 = ss.y + ss.dy * ss.age;
        const x0 = x1 - ss.dx * tailFrames;
        const y0 = y1 - ss.dy * tailFrames;
        const grad = bgCtx.createLinearGradient(x0, y0, x1, y1);
        grad.addColorStop(0, 'rgba(255,255,255,0)');
        grad.addColorStop(1, `rgba(255,255,255,${(alpha * 0.92).toFixed(3)})`);
        bgCtx.beginPath();
        bgCtx.moveTo(x0, y0);
        bgCtx.lineTo(x1, y1);
        bgCtx.strokeStyle = grad;
        bgCtx.lineWidth   = ss.size;
        bgCtx.globalAlpha = 1;
        bgCtx.stroke();
        ss.age++;
      }
    }

    function getProjection() {
      return d3.geoOrthographic()
        .scale(Math.min(W, H) * 0.44 * zoom)
        .translate([W / 2, H / 2])
        .rotate(rotation)
        .clipAngle(90);
    }

    function globeRadius(): number {
      return Math.min(W, H) * 0.44 * zoom;
    }

    function isOnGlobe(x: number, y: number): boolean {
      const r = globeRadius();
      return (x - W / 2) ** 2 + (y - H / 2) ** 2 <= r * r;
    }

    function createParticle(): Particle {
      let x = Math.random() * W;
      const y = Math.random() * H;
      return {
        x, y, px: x, py: y,
        age:    Math.floor(Math.random() * MAX_AGE),
        maxAge: MAX_AGE * (0.5 + Math.random() * 0.5),
        speed:  0,
        dirU: (Math.random() - 0.5) * 8,
        dirV: (Math.random() - 0.5) * 8,
      };
    }

    function initParticles() {
      particles = Array.from(
        { length: settingsRef.current.particleCount },
        createParticle
      );
    }

    function stepParticles() {
      if (!settingsRef.current.showWind) return;
      if (!wind) return;
      const proj = getProjection();
      for (let i = 0; i < particles.length; i++) {
        const pt = particles[i];
        pt.age++;
        if (pt.age > pt.maxAge || !isOnGlobe(pt.x, pt.y)) {
          particles[i] = createParticle();
          continue;
        }
        const ll = proj.invert?.([pt.x, pt.y]);
        if (!ll) { particles[i] = createParticle(); continue; }
        const [lon, lat] = ll;
        const uv = interpolateWind(wind, lon, lat);
        if (!uv) { particles[i] = createParticle(); continue; }
        const latRad = lat * (Math.PI / 180);

        const animMode = (settingsRef.current as any).animMode ?? 'wind';
        const reverse  = (settingsRef.current as any).reverseFlow ? -1 : 1;
        const cosLat   = Math.max(Math.cos(latRad), 0.01);
        let dLon: number, dLat: number;

        if (animMode === 'straight') {
          // Particles flow along latitude lines
          const STRAIGHT = 5; // m/s equivalent
          dLon  = reverse * (STRAIGHT * settingsRef.current.timeScale / (EARTH_R * cosLat))
                  * (180 / Math.PI);
          dLat  = 0;
          pt.speed = STRAIGHT;
        } else if (animMode === 'random') {
          // Each particle follows its own stored direction
          dLon  = reverse * (pt.dirU * settingsRef.current.timeScale / (EARTH_R * cosLat))
                  * (180 / Math.PI);
          dLat  = reverse * (pt.dirV * settingsRef.current.timeScale / EARTH_R)
                  * (180 / Math.PI);
          pt.speed = Math.sqrt(pt.dirU ** 2 + pt.dirV ** 2);
        } else {
          // Normal wind mode — use GFS data
          const [u, v] = uv;
          dLon  = reverse * (u * settingsRef.current.timeScale / (EARTH_R * cosLat))
                  * (180 / Math.PI);
          dLat  = reverse * (v * settingsRef.current.timeScale / EARTH_R)
                  * (180 / Math.PI);
          pt.speed = Math.sqrt(u * u + v * v);
        }

        const newPos = proj([lon + dLon, lat + dLat]);
        if (!newPos) { particles[i] = createParticle(); continue; }
        pt.px    = pt.x;
        pt.py    = pt.y;
        pt.x     = newPos[0];
        pt.y     = newPos[1];
        pt.color = windColor(pt.speed, settingsRef.current.colorTheme);
      }
    }

    function drawGlobe() {
      const proj = getProjection();
      const path = d3.geoPath(proj, gCtx);
      const r    = proj.scale() as number;
      const cx   = W / 2, cy = H / 2;
      const lx   = cx - r * 0.28;
      const ly   = cy - r * 0.38;

      const earthTex   = planetTextures['earth'];
      const earthReady = earthTex?.complete && (earthTex?.naturalWidth ?? 0) > 0;

      gCtx.clearRect(0, 0, W, H);
      gCtx.globalAlpha = settingsRef.current.globeBrightness;

      const planet = settingsRef.current.planetSurface;

      if (planet === 'earth') {
        if (!earthReady) {
          // Gradient fallback while texture loads
          gCtx.save();
          gCtx.beginPath();
          gCtx.arc(cx, cy, r, 0, Math.PI * 2);
          const oceanGrad = gCtx.createRadialGradient(lx, ly, r * 0.05, cx, cy, r);
          oceanGrad.addColorStop(0,    '#1e4a8c');
          oceanGrad.addColorStop(0.4,  '#0d2a5e');
          oceanGrad.addColorStop(0.75, '#061535');
          oceanGrad.addColorStop(1,    '#010810');
          gCtx.fillStyle = oceanGrad;
          gCtx.fill();
          gCtx.restore();

          if (world) {
            gCtx.save();
            gCtx.beginPath();
            path(feature(world, world.objects.land) as any);
            const landGrad = gCtx.createRadialGradient(lx, ly, r * 0.05, cx, cy, r);
            landGrad.addColorStop(0,    '#3d5068');
            landGrad.addColorStop(0.4,  '#243448');
            landGrad.addColorStop(0.75, '#111e2e');
            landGrad.addColorStop(1,    '#060c18');
            gCtx.fillStyle = landGrad;
            gCtx.fill();
            gCtx.beginPath();
            path(d3.geoGraticule()());
            gCtx.strokeStyle = 'rgba(255,255,255,0.07)';
            gCtx.lineWidth   = 0.35;
            gCtx.stroke();
            gCtx.beginPath();
            path(mesh(world, world.objects.countries,
              (a: any, b: any) => a !== b) as any);
            gCtx.strokeStyle = 'rgba(255,255,255,0.28)';
            gCtx.lineWidth   = 0.5;
            gCtx.stroke();
            gCtx.restore();
          }

        } else {
          // NASA Blue Marble texture
          gCtx.save();
          gCtx.beginPath();
          gCtx.arc(cx, cy, r, 0, Math.PI * 2);
          gCtx.fillStyle = '#001025';
          gCtx.fill();
          gCtx.restore();

          updateTextureOffscreen();

          if (texOffscreen && lastTexPlanet === 'earth') {
            gCtx.save();
            gCtx.beginPath();
            gCtx.arc(cx, cy, r, 0, Math.PI * 2);
            gCtx.clip();
            gCtx.imageSmoothingEnabled = true;
            gCtx.imageSmoothingQuality = 'high';
            gCtx.drawImage(texOffscreen, cx - r, cy - r, r * 2, r * 2);
            gCtx.restore();
          }

          // Country borders and graticule over the photo texture
          if (world) {
            gCtx.save();
            gCtx.beginPath();
            path(d3.geoGraticule()());
            gCtx.strokeStyle = 'rgba(255,255,255,0.06)';
            gCtx.lineWidth   = 0.3;
            gCtx.stroke();
            gCtx.beginPath();
            path(mesh(world, world.objects.countries,
              (a: any, b: any) => a !== b) as any);
            gCtx.strokeStyle = 'rgba(255,255,255,0.22)';
            gCtx.lineWidth   = 0.5;
            gCtx.stroke();
            gCtx.restore();
          }
        }

      } else {
        // ── Planet: per-pixel spherical projection ─────────────────────

        // Dark base while texture loads
        gCtx.save();
        gCtx.beginPath();
        gCtx.arc(cx, cy, r, 0, Math.PI * 2);
        gCtx.fillStyle = '#080808';
        gCtx.fill();
        gCtx.restore();

        // Render texture correctly projected onto sphere surface
        updateTextureOffscreen();

        if (texOffscreen && lastTexPlanet === settingsRef.current.planetSurface) {
          gCtx.save();
          gCtx.beginPath();
          gCtx.arc(cx, cy, r, 0, Math.PI * 2);
          gCtx.clip();
          gCtx.imageSmoothingEnabled = true;
          gCtx.imageSmoothingQuality = 'high';
          gCtx.drawImage(texOffscreen, cx - r, cy - r, r * 2, r * 2);
          gCtx.restore();
        }

        // Faint graticule coordinate grid
        if (world) {
          gCtx.save();
          const gp = d3.geoPath(proj, gCtx);
          gCtx.beginPath();
          gp(d3.geoGraticule()());
          gCtx.strokeStyle = 'rgba(255,255,255,0.08)';
          gCtx.lineWidth   = 0.3;
          gCtx.stroke();
          gCtx.restore();
        }
      }

      // ── Sphere shading (all planets except textured Earth) ───────────
      if (planet === 'earth' && !earthReady) {
        const shadeGrad = gCtx.createRadialGradient(lx, ly, 0, cx, cy, r * 1.05);
        shadeGrad.addColorStop(0,    'rgba(255,255,255,0.09)');
        shadeGrad.addColorStop(0.3,  'rgba(255,255,255,0.03)');
        shadeGrad.addColorStop(0.65, 'rgba(0,0,0,0)');
        shadeGrad.addColorStop(0.85, 'rgba(0,0,15,0.18)');
        shadeGrad.addColorStop(1,    'rgba(0,0,15,0.42)');
        gCtx.save();
        gCtx.beginPath();
        gCtx.arc(cx, cy, r, 0, Math.PI * 2);
        gCtx.clip();
        gCtx.fillStyle = shadeGrad;
        gCtx.fillRect(cx - r, cy - r, r * 2, r * 2);
        gCtx.restore();
      }

      gCtx.globalAlpha = 1;

      // ── Globe rim ─────────────────────────────────────────────────────
      gCtx.save();
      gCtx.beginPath();
      gCtx.arc(cx, cy, r, 0, Math.PI * 2);
      gCtx.strokeStyle = 'rgba(255,255,255,0.1)';
      gCtx.lineWidth   = 1;
      gCtx.stroke();
      gCtx.restore();

      // ── Atmospheric glow ──────────────────────────────────────────────
      const glowHue     = (settingsRef.current as any).glowHue     ?? 0;
      const glowOpacity = (settingsRef.current as any).glowOpacity ?? 1.0;

      if (glowOpacity > 0) {
        gCtx.save();
        if (glowHue !== 0) gCtx.filter = `hue-rotate(${glowHue}deg)`;

        const go = glowOpacity;
        const atmoOuter = gCtx.createRadialGradient(
          cx, cy, r * 0.92, cx, cy, r * 1.22);
        atmoOuter.addColorStop(0,    'rgba(0,0,0,0)');
        atmoOuter.addColorStop(0.45, `rgba(40,110,255,${+(0.04*go).toFixed(3)})`);
        atmoOuter.addColorStop(0.75, `rgba(70,160,255,${+(0.10*go).toFixed(3)})`);
        atmoOuter.addColorStop(0.9,  `rgba(100,190,255,${+(0.06*go).toFixed(3)})`);
        atmoOuter.addColorStop(1,    'rgba(130,210,255,0)');
        gCtx.beginPath();
        gCtx.arc(cx, cy, r * 1.22, 0, Math.PI * 2);
        gCtx.fillStyle = atmoOuter;
        gCtx.fill();

        const atmoLimb = gCtx.createRadialGradient(
          cx, cy, r * 0.95, cx, cy, r * 1.06);
        atmoLimb.addColorStop(0,    'rgba(0,0,0,0)');
        atmoLimb.addColorStop(0.55, `rgba(80,170,255,${+(0.10*go).toFixed(3)})`);
        atmoLimb.addColorStop(0.82, `rgba(140,210,255,${+(0.28*go).toFixed(3)})`);
        atmoLimb.addColorStop(0.95, `rgba(180,230,255,${+(0.12*go).toFixed(3)})`);
        atmoLimb.addColorStop(1,    'rgba(200,240,255,0)');
        gCtx.beginPath();
        gCtx.arc(cx, cy, r * 1.06, 0, Math.PI * 2);
        gCtx.fillStyle = atmoLimb;
        gCtx.fill();

        gCtx.restore();
      }
    }

    function drawParticles() {
      const r  = globeRadius();
      const cx = W / 2, cy = H / 2;

      if (r <= 1) return;   // prevents arc error on init frames

      if (!settingsRef.current.showWind) {
        pCtx.clearRect(0, 0, W, H);
        return;
      }

      // Rebuild bucket colors if theme changed
      if (settingsRef.current.colorTheme !== lastTheme) {
        lastTheme = settingsRef.current.colorTheme;
        BUCKET_COLORS = Array.from({ length: N_COLOR_BUCKETS }, (_, b) =>
          windColor((b + 0.5) * (MAX_WIND_SPEED / N_COLOR_BUCKETS), lastTheme)
        );
      }

      // Only fade when wind data is present and particles are active
      if (wind && particles.length > 0) {
        pCtx.globalCompositeOperation = 'destination-out';
        pCtx.fillStyle = `rgba(0, 0, 0, ${settingsRef.current.trailFade})`;
        pCtx.fillRect(0, 0, W, H);
        pCtx.globalCompositeOperation = 'source-over';
      }

      if (!wind || particles.length === 0) return;

      // Clip strokes to globe sphere
      pCtx.save();
      pCtx.beginPath();
      pCtx.arc(cx, cy, r - 0.5, 0, Math.PI * 2);
      pCtx.clip();

      // Batch by color bucket — N_COLOR_BUCKETS stroke() calls total
      const buckets: number[][] =
        Array.from({ length: N_COLOR_BUCKETS }, () => []);

      for (const pt of particles) {
        if (pt.age < 1) continue;
        const bucket = Math.min(
          Math.floor((pt.speed / MAX_WIND_SPEED) * N_COLOR_BUCKETS),
          N_COLOR_BUCKETS - 1
        );
        buckets[bucket].push(pt.px, pt.py, pt.x, pt.y);
      }

      pCtx.lineWidth   = settingsRef.current.trailWidth;
      pCtx.globalAlpha = settingsRef.current.particleOpacity;

      for (let b = 0; b < N_COLOR_BUCKETS; b++) {
        const coords = buckets[b];
        if (coords.length === 0) continue;
        pCtx.beginPath();
        for (let i = 0; i < coords.length; i += 4) {
          pCtx.moveTo(coords[i],     coords[i + 1]);
          pCtx.lineTo(coords[i + 2], coords[i + 3]);
        }
        pCtx.strokeStyle = BUCKET_COLORS[b];
        pCtx.stroke();
      }

      pCtx.globalAlpha = 1;
      pCtx.restore();
    }

    function tick(timestamp: number) {
      if (!settingsRef.current.showWind && !dragging) {
  rotation[0] = (rotation[0] + 0.05) % 360;
}
      if ((settingsRef.current as any).rebuildParticles) {
        particles = Array.from(
          { length: settingsRef.current.particleCount },
          createParticle
        );
        (settingsRef.current as any).rebuildParticles = false;
      }
      drawBackground(timestamp);
      stepParticles();
      drawGlobe();
      drawParticles();
      rafRef.current = requestAnimationFrame(tick);
    }

    // Start animation immediately — globe renders as soon as component mounts
    if (!running) {
      running = true;
      rafRef.current = requestAnimationFrame(tick);
    }

    // Load world topology — try local file first, fall back to CDN
    const loadWorld = async () => {
      try {
        const localRes = await fetch('/world-110m.json');
        if (localRes.ok) {
          world = await localRes.json();
          return;
        }
      } catch {
        // local file not found, try CDN
      }
      try {
        const cdnRes = await fetch(WORLD_TOPO_URL);
        if (cdnRes.ok) {
          world = await cdnRes.json();
        }
      } catch {
        // world topology unavailable — globe renders as ocean only
      }
    };

    // Load wind data from API
    const loadWind = async () => {
      try {
        const res = await fetch('/api/wind');
        if (!res.ok) {
          console.warn('[Globe] /api/wind returned', res.status);
          return;
        }
        const data = await res.json();
        // Validate the response has the expected shape
        if (
          data &&
          data.header &&
          typeof data.header.nx === 'number' &&
          typeof data.header.ny === 'number' &&
          Array.isArray(data.uData) &&
          Array.isArray(data.vData) &&
          data.uData.length >= data.header.nx * data.header.ny * 0.9 &&
          data.vData.length >= data.header.nx * data.header.ny * 0.9
        ) {
          wind = data;
          initParticles();
          const el = document.getElementById('wind-loading');
          if (el) el.style.opacity = '0';
          console.log(
            `[Globe] wind loaded (${data.source ?? 'gfs'}): ${data.uData.length} points`
          );
        } else {
          console.warn('[Globe] wind data shape invalid:', {
            hasHeader: !!data?.header,
            uLen: data?.uData?.length,
            vLen: data?.vData?.length,
          });
        }
      } catch (err) {
        console.warn('[Globe] wind fetch error:', err);
      }
    };

    // Load data in parallel, animation already running
    Promise.all([loadWorld(), loadWind()]).then(() => {
      onReady?.();
    }).catch(() => {});

    // Mouse drag
    pCanvas.addEventListener('mousedown', e => {
      dragging = true;
      lastX = e.clientX; lastY = e.clientY;
      pCanvas.style.cursor = 'grabbing';
    });
    window.addEventListener('mousemove', e => {
      if (dragging) {
        const dx   = e.clientX - lastX;
        const dy   = e.clientY - lastY;
        const sens = 60 / globeRadius();
        rotation[0] += dx * sens;
        rotation[1]  = Math.max(-85, Math.min(85, rotation[1] - dy * sens));
        lastX = e.clientX; lastY = e.clientY;
        hideReadout();
      } else {
        updateReadout(e.clientX, e.clientY);
      }
    });
    window.addEventListener('mouseup', () => {
      dragging = false;
      pCanvas.style.cursor = 'grab';
    });
    pCanvas.addEventListener('mouseleave', hideReadout);
    window.addEventListener('mouseup',     hideReadout);

    // Mouse wheel zoom
    pCanvas.addEventListener('wheel', e => {
      e.preventDefault();
      zoom = Math.max(0.25, Math.min(10, zoom * (1 - e.deltaY * 0.0008)));
    }, { passive: false });

    // Touch drag and pinch zoom
    pCanvas.addEventListener('touchstart', e => {
      if (e.touches.length === 1) {
        dragging = true;
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;
      }
      if (e.touches.length === 2) {
        dragging = false;
        lastPinch = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
      }
    }, { passive: true });

    pCanvas.addEventListener('touchmove', e => {
      e.preventDefault();
      if (e.touches.length === 1 && dragging) {
        const dx   = e.touches[0].clientX - lastX;
        const dy   = e.touches[0].clientY - lastY;
        const sens = 60 / globeRadius();
        rotation[0] += dx * sens;
        rotation[1]  = Math.max(-85, Math.min(85, rotation[1] - dy * sens));
        lastX = e.touches[0].clientX;
        lastY = e.touches[0].clientY;
      }
      if (e.touches.length === 2) {
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        zoom      = Math.max(0.25, Math.min(10, zoom * (dist / lastPinch)));
        lastPinch = dist;
      }
      if (e.touches.length === 1 && !dragging) {
        updateReadout(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });

    pCanvas.addEventListener('touchend', () => {
      dragging = false;
      setTimeout(hideReadout, 1800);
    });

    // ── Wind readout helpers ───────────────────────────────────────────

    const DIRS = ['N','NNE','NE','ENE','E','ESE','SE','SSE',
                  'S','SSW','SW','WSW','W','WNW','NW','NNW'];

    const BEAUFORT: [number, string][] = [
      [0.3,  'Calm'],          [1.5,  'Light air'],
      [3.3,  'Light breeze'],  [5.4,  'Gentle breeze'],
      [7.9,  'Moderate breeze'],[10.7, 'Fresh breeze'],
      [13.8, 'Strong breeze'], [17.1, 'Near gale'],
      [20.7, 'Gale'],          [24.4, 'Strong gale'],
      [28.4, 'Storm'],         [32.6, 'Violent storm'],
      [Infinity, 'Hurricane force'],
    ];

    function updateReadout(clientX: number, clientY: number) {
      const el = readoutRef.current;
      if (!el || !wind) { hideReadout(); return; }

      const proj = getProjection();
      const ll   = proj.invert?.([clientX, clientY]);
      if (!ll) { hideReadout(); return; }

      const uv = interpolateWind(wind, ll[0], ll[1]);
      if (!uv) { hideReadout(); return; }

      const [u, v] = uv;
      const speed  = Math.sqrt(u * u + v * v);
      const dirDeg = ((Math.atan2(-u, -v) * 180 / Math.PI) + 360) % 360;
      const compass = DIRS[Math.round(dirDeg / 22.5) % 16];
      const desc    = BEAUFORT.find(([lim]) => speed <= lim)?.[1] ?? 'Hurricane force';
      const kmh     = (speed * 3.6).toFixed(0);
      const knots   = (speed * 1.944).toFixed(0);

      const s = el.querySelector('.rw-speed')    as HTMLElement;
      const d = el.querySelector('.rw-dir')      as HTMLElement;
      let b = el.querySelector('.rw-desc')     as HTMLElement;
      const k = el.querySelector('.rw-units')    as HTMLElement;

      if (s) s.textContent = speed.toFixed(1);
      if (d) d.textContent = compass;
      if (b) b.textContent = desc;
      if (k) k.textContent = `${kmh} km/h · ${knots} kn`;

      // Position above and to the right of cursor, clamped to viewport
      const PAD = 10, EL_W = 175, EL_H = 80;
      el.style.left    = `${Math.min(clientX + 18, W - EL_W - PAD)}px`;
      el.style.top     = `${Math.max(clientY - EL_H - 14, PAD)}px`;
      el.style.opacity = '1';
    }

    function hideReadout() {
      if (readoutRef.current) readoutRef.current.style.opacity = '0';
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#00000a' }}>
      <canvas
        ref={bgCanvasRef}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />
      <canvas
        ref={globeCanvasRef}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />
      <ControlPanel
        loaded8kCount={num8kLoaded}
        onChange={patch => {
          console.log('[globe] Setting changed:', patch);
          Object.assign(settingsRef.current, patch);
          if ('textureQuality' in patch) {
            (settingsRef.current as any).needsTextureReload = true;
          }
          if ('particleCount' in patch) {
            (settingsRef.current as any).rebuildParticles = true;
          }
        }}
        num8kLoaded={num8kLoaded}
      />
      <canvas
        ref={particleCanvasRef}
        style={{
          position: 'absolute', inset: 0,
          cursor: 'grab', touchAction: 'none',
        }}
      />

      {/* ── Export PNG + Share icon-only button cluster ─────────────── */}
      <div style={{
        position: 'fixed',
        bottom:   '24px',
        right:    '20px',
        zIndex:   1001,
        display:  'flex',
        alignItems: 'center',
        gap:      '6px',
      }}>
        {/* Export PNG icon button */}
        <button
          onClick={captureGlobe}
          disabled={capturing || sharing}
          aria-label="Export globe view as PNG"
          title="Export PNG"
          style={{
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            width:           '38px',
            height:          '38px',
            background:      capturing ? 'rgba(0,229,204,0.12)' : 'rgba(4,10,28,0.82)',
            backdropFilter:  'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border:          `1px solid ${capturing ? 'rgba(0,229,204,0.5)' : 'rgba(0,229,204,0.22)'}`,
            borderRadius:    '10px',
            padding:         0,
            cursor:          (capturing || sharing) ? 'default' : 'pointer',
            transition:      'background 0.2s, border-color 0.2s, opacity 0.2s',
            opacity:         (capturing || sharing) ? 0.7 : 1,
          }}
        >
          <svg
            width="17" height="17" viewBox="0 0 24 24"
            fill="none" stroke={capturing ? '#00e5cc' : 'rgba(255,255,255,0.75)'}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
            <circle cx="12" cy="13" r="4"/>
          </svg>
        </button>

        {/* Share icon button */}
        <button
          onClick={shareGlobe}
          disabled={capturing || sharing}
          aria-label="Share globe view"
          title="Share"
          style={{
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'center',
            width:           '38px',
            height:          '38px',
            background:      sharing ? 'rgba(0,229,204,0.12)' : 'rgba(4,10,28,0.82)',
            backdropFilter:  'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            border:          `1px solid ${sharing ? 'rgba(0,229,204,0.5)' : 'rgba(0,229,204,0.22)'}`,
            borderRadius:    '10px',
            padding:         0,
            cursor:          (capturing || sharing) ? 'default' : 'pointer',
            transition:      'background 0.2s, border-color 0.2s, opacity 0.2s',
            opacity:         (capturing || sharing) ? 0.7 : 1,
          }}
        >
          <svg
            width="17" height="17" viewBox="0 0 24 24"
            fill="none" stroke={sharing ? '#00e5cc' : 'rgba(255,255,255,0.75)'}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
            <polyline points="16 6 12 2 8 6"/>
            <line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
        </button>
      </div>

      {/* Wind speed readout */}
      <div
        ref={readoutRef}
        style={{
          position:           'fixed',
          opacity:            0,
          transition:         'opacity 0.18s ease',
          background:         'rgba(4,10,28,0.90)',
          border:             '1px solid rgba(255,255,255,0.13)',
          borderRadius:       '10px',
          padding:            '10px 14px 9px',
          pointerEvents:      'none',
          backdropFilter:     'blur(16px)',
          WebkitBackdropFilter:'blur(16px)',
          zIndex:             998,
          minWidth:           '165px',
          fontFamily:         'system-ui, -apple-system, sans-serif',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px', marginBottom: '3px' }}>
          <span className="rw-speed"
            style={{ fontSize: '22px', fontWeight: 700, color: '#ffffff', lineHeight: 1 }}>
            --
          </span>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginRight: '6px' }}>
            m/s
          </span>
          <span className="rw-dir"
            style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.75)', marginLeft: 'auto' }}>
            --
          </span>
        </div>
        <div className="rw-desc"
          style={{ fontSize:'11px', color:'rgba(255,255,255,0.5)',
                   letterSpacing:'0.03em', marginBottom:'2px' }}>
          --
        </div>
        <div className="rw-units"
          style={{ fontSize:'10px', color:'rgba(255,255,255,0.25)',
                   letterSpacing:'0.03em' }}>
          --
        </div>
      </div>
      {/* Loading indicator — shown until wind data arrives */}
      <div
        id="wind-loading"
        style={{
          position: 'absolute',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.45)',
          fontSize: '13px',
          fontFamily: 'system-ui, sans-serif',
          letterSpacing: '0.04em',
          pointerEvents: 'none',
          transition: 'opacity 0.8s ease',
        }}
      >
        loading wind data from NOAA…
      </div>
    </div>
  );
}